import {
  Box,
  Button,
  Checkbox,
  createStyles,
  Group,
  Modal,
  Text,
} from "@mantine/core";
import React, { useState } from "react";
import { LuFileCheck2 } from "react-icons/lu";
import { updateProfile } from "../../services/profile";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const useStyles = createStyles(() => ({
  header: {
    display: "flex",
    gap: "14px",
    padding: "22px 26px 14px",
    borderBottom: "1px solid var(--app-border)",
    alignItems: "flex-start",
  },

  iconBox: {
    width: "44px",
    height: "44px",
    minWidth: "44px",
    borderRadius: "10px",
    background: "var(--app-tint-amber)",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
  },

  headerTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 700,
    color: "var(--app-text)",
    lineHeight: 1.3,
  },

  headerDesc: {
    margin: "4px 0 0",
    fontSize: "13px",
    color: "var(--app-text-muted)",
    lineHeight: 1.5,
  },

  body: {
    padding: "18px 26px",
    overflowY: "auto",
    flex: 1,
  },

  tcContent: {
    border: "1px solid var(--app-border)",
    borderRadius: "10px",
    padding: "18px 20px",
    background: "var(--app-surface-2)",
    fontSize: "13px",
    color: "var(--app-text)",
    lineHeight: 1.65,
    maxHeight: "360px",
    overflowY: "auto",
    fontFamily: "Inter, sans-serif",

    "& h3": {
      margin: "0 0 8px",
      fontSize: "16px",
      color: "var(--app-text)",
      fontWeight: 800,
      letterSpacing: "-0.005em",
    },

    "& h4": {
      margin: "18px 0 6px",
      fontSize: "13.5px",
      color: "var(--app-text)",
      fontWeight: 700,
    },

    "& h4:first-of-type": {
      marginTop: 0,
    },

    "& h5": {
      margin: "12px 0 4px",
      fontSize: "12.5px",
      color: "var(--app-text)",
      fontWeight: 700,
    },

    "& p": {
      margin: "0 0 10px",
      fontSize: "13px",
      color: "var(--app-text)",
    },

    "& p:last-child": {
      marginBottom: 0,
    },

    "& ul": {
      margin: "0 0 12px",
      paddingLeft: "22px",
    },

    "& ul li": {
      marginBottom: "4px",
    },

    "& .tc-effective": {
      fontWeight: 700,
      color: "var(--app-text)",
      marginBottom: "12px",
    },

    "& .tc-contact b": {
      color: "var(--app-text)",
    },
  },

  tcCheck: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "12px 14px",
    background: "var(--app-tint-amber)",
    border: "1px solid #FAE7BE",
    borderRadius: "10px",
    marginTop: "16px",
  },

  checkboxLabel: {
    fontSize: "13px",
    color: "var(--app-text)",
    lineHeight: 1.5,
    fontFamily: "Inter, sans-serif",
    cursor: "pointer",
    "& b": {
      color: "var(--app-text)",
    },
  },

  footer: {
    padding: "14px 26px 22px",
    borderTop: "1px solid var(--app-border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },

  footerNote: {
    fontSize: "12px",
    color: "var(--app-text-muted)",
  },

  cancelBtn: {
    border: "1px solid var(--app-border)",
    color: "var(--app-text)",
    background: "var(--app-surface)",
    "&:hover": {
      background: "var(--app-border)",
    },
  },

  agreeBtn: {
    background: "var(--app-primary)",
    color: "var(--app-on-primary)",
    fontWeight: 600,
    "&:hover": {
      background: "var(--app-primary)",
    },
    "&:disabled": {
      background: "var(--app-border)",
      color: "var(--app-text-muted)",
    },
  },
}));

const BillingAgree = ({ open, setOpen }) => {
  const [isChecked, setIsChecked] = useState(false);
  const { profile } = useSelector((state) => state?.profile);
  const { classes } = useStyles();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleClose = () => {
    setOpen(false);
  };

  const handleCheckboxChange = (event) => {
    setIsChecked(event.currentTarget.checked);
  };

  const submit = async () => {
    setLoading(true);
    try {
      let payload = {
        ...profile,
        billingAgree: isChecked,
        billingAgreeDate: new Date().toISOString(),
      };
      let response = await updateProfile(payload);
      if (response) {
        handleClose();
        navigate("/home/billing-claim-form");
      }
      setLoading(false);
    } catch (error) {
      console.log("error", error);
    }
  };

  return (
    <Modal
      opened={open}
      size={640}
      shadow="md"
      onClose={handleClose}
      overlayProps={{ blur: 3 }}
      radius="lg"
      zIndex={1000}
      withCloseButton={false}
      centered
      styles={{
        body: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.iconBox}>
          <LuFileCheck2 size={22} color="#B7791F" />
        </Box>
        <Box>
          <Text component="h2" className={classes.headerTitle}>
            Billing terms &amp; conditions
          </Text>
          <Text className={classes.headerDesc}>
            Please review and acknowledge before starting your billing setup.
            We'll only ask once. Your acknowledgement is recorded for this
            account.
          </Text>
        </Box>
      </Box>

      {/* Body */}
      <Box className={classes.body}>
        {/* T&C Content */}
        <Box className={classes.tcContent}>
          <h3>Ogiso.io Billing Terms and Conditions</h3>
          <p className="tc-effective">Effective Date: 05/01/2025</p>

          <p>
            These Terms and Conditions ("Terms") govern your use of the
            Ogiso.io billing platform ("Service"), operated by Ogiso, Inc.
            ("Ogiso," "we," "us," or "our"). By accessing or using our billing
            services, you agree to be bound by these Terms. If you do not agree
            to these Terms, you may not use the Service.
          </p>

          <h4>1. Billing Services Overview</h4>
          <p>
            Ogiso.io provides billing infrastructure for healthcare providers
            and facilities, enabling claims submission to Medicaid and other
            insurance providers, as well as direct patient invoicing and payment
            collection.
          </p>
          <p>Services may include:</p>
          <ul>
            <li>Insurance claims management</li>
            <li>Medicaid claims submission</li>
            <li>Invoice generation and delivery to patients</li>
            <li>Payment processing via secure gateways</li>
            <li>Account reconciliation and reporting</li>
            <li>Claims Verification</li>
          </ul>

          <h4>2. Eligibility</h4>
          <ul>
            <li>
              You are authorized to act on behalf of a healthcare provider or
              facility.
            </li>
            <li>
              You are compliant with HIPAA and all relevant local, state, and
              federal healthcare regulations.
            </li>
            <li>
              You provided the services you are submitting the claim for.
            </li>
          </ul>

          <h4>3. Payment Terms</h4>
          <h5>3.1 Invoice Generation</h5>
          <p>
            Invoices generated through Ogiso.io must accurately reflect
            services rendered, with complete and truthful data.
          </p>
          <h5>3.2 Insurance Claims</h5>
          <p>
            Ogiso.io facilitates the submission of claims but does not
            guarantee reimbursement. All reimbursements are subject to payer
            review and policy.
          </p>
          <h5>3.3 Patient Payments</h5>
          <p>
            Ogiso.io may facilitate patient invoicing and payment collection. It
            is the responsibility of the provider to ensure that patients are
            informed of their financial responsibility.
          </p>
          <h5>3.4 Transaction Fees</h5>
          <p>
            Ogiso.io may charge fees for billing transactions, payment
            processing, invoice processing or service subscriptions. All fees
            will be communicated in advance and reflected in your billing
            dashboard.
          </p>

          <h4>4. Compliance and Data Privacy</h4>
          <h5>4.1 HIPAA</h5>
          <p>
            All data entered or processed through Ogiso.io must comply with
            HIPAA regulations. We implement administrative, technical, and
            physical safeguards to protect PHI (Protected Health Information).
          </p>
          <h5>4.2 Data Ownership</h5>
          <p>
            You retain ownership of all data entered into the system. Ogiso.io
            acts as a data processor and will not share or sell your data.
          </p>

          <h4>5. Account Management</h4>
          <ul>
            <li>
              You are responsible for keeping your account credentials secure.
            </li>
            <li>Notify us immediately in case of unauthorized use.</li>
          </ul>

          <h4>6. Termination</h4>
          <p>
            Ogiso.io reserves the right to suspend or terminate your access to
            billing services for:
          </p>
          <ul>
            <li>Violation of these Terms.</li>
            <li>Fraudulent billing activities.</li>
            <li>Failure to pay applicable fees.</li>
          </ul>

          <h4>7. Disclaimers and Limitations</h4>
          <ul>
            <li>
              Ogiso.io is not responsible for denied claims or failed
              reimbursements.
            </li>
            <li>We provide no guarantee on payment timelines.</li>
            <li>Service is "as is" and "as available."</li>
          </ul>

          <h4>8. Modifications</h4>
          <p>
            We reserve the right to update these Terms at any time. Updated
            versions will be posted on our website with an updated effective
            date. Continued use of the Service after changes constitutes
            acceptance.
          </p>

          <h4>9. Governing Law</h4>
          <p>
            These Terms are governed by and shall be construed in accordance
            with the laws of the State of Arizona, where Ogiso, Inc. is
            headquartered, and the laws of the state in which the healthcare
            provider or facility using the Service operates, without regard to
            conflict of law principles. Users of the Service must also comply
            with all applicable federal laws and regulations, including but not
            limited to HIPAA, CMS guidelines, and applicable healthcare billing
            standards.
          </p>

          <h4>Dispute Resolution</h4>
          <p>
            In the event of any dispute, controversy, or claim arising out of
            or relating to these Terms, the parties agree to first attempt to
            resolve the matter through good faith negotiations. If resolution
            cannot be reached within 30 days, the parties agree to submit the
            dispute to binding arbitration under the rules of the American
            Arbitration Association (AAA). The arbitration will take place in
            Phoenix, Arizona, or a mutually agreed location, and the decision of
            the arbitrator(s) shall be final and enforceable in any court of
            competent jurisdiction.
          </p>
          <p>
            Nothing in this section prevents either party from seeking
            injunctive or equitable relief in a court of law where appropriate,
            especially in matters involving unauthorized data access or misuse
            of intellectual property.
          </p>

          <h4>10. Contact</h4>
          <p>
            For questions or concerns regarding billing or these Terms, contact
            us at:
          </p>
          <p className="tc-contact">
            <b>Ogiso, Inc.</b>
            <br />
            <b>Email:</b> support@ogiso.io
            <br />
            <b>Phone:</b> 757-600-0297
          </p>
        </Box>

        {/* Checkbox Area */}
        <Box className={classes.tcCheck}>
          <Checkbox
            checked={isChecked}
            onChange={handleCheckboxChange}
            color="orange"
            mt={3}
          />
          <label className={classes.checkboxLabel} onClick={() => setIsChecked(!isChecked)}>
            <b>I have read and understood the terms and conditions.</b>
            <br />
            I acknowledge that fees will be deducted from paid claim
            remittances and that I&apos;m responsible for the accuracy of
            submitted claims.
          </label>
        </Box>
      </Box>

      {/* Footer */}
      <Box className={classes.footer}>
        <Text className={classes.footerNote}>
          Required to start billing setup
        </Text>
        <Group spacing="sm">
          <Button
            variant="default"
            className={classes.cancelBtn}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={!isChecked}
            className={classes.agreeBtn}
            onClick={submit}
          >
            Acknowledge &amp; start setup
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};

export default BillingAgree;
